import { ChartTick } from '../types';

type NumericDomain = [number, number];

export type LinearScale = (value: number) => number;

/** Creates evenly spaced tick positions and optional formatted labels for a numeric domain. */
export const createTicks = (
  domainStart: number,
  domainEnd: number,
  count: number,
  scale: LinearScale,
  format?: (value: number) => string,
): ChartTick[] => {
  if (count <= 0) return [];

  if (count === 1 || domainStart === domainEnd) {
    return [{ position: scale(domainStart), label: format?.(domainStart) }];
  }

  const step = (domainEnd - domainStart) / (count - 1);

  return Array.from({ length: count }, (_, index) => {
    const value = domainStart + step * index;
    return { position: scale(value), label: format?.(value) };
  });
};

/** Maps values from a numeric domain to a numeric range with a linear interpolation. */
export function createLinearScale(domain: NumericDomain, range: NumericDomain): LinearScale {
  const [domainStart, domainEnd] = domain;
  const [rangeStart, rangeEnd] = range;
  const domainLength = domainEnd - domainStart;

  if (domainLength === 0) {
    const rangeMidpoint = rangeStart + (rangeEnd - rangeStart) / 2;
    return () => rangeMidpoint;
  }

  const rangeLength = rangeEnd - rangeStart;
  return (value) => rangeStart + ((value - domainStart) / domainLength) * rangeLength;
}

/** Maps a value from a linear scale's range back to its domain. */
export const invertLinearScale = (value: number, domain: NumericDomain, range: NumericDomain) =>
  createLinearScale(range, domain)(value);
